import React, { useState } from "react"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCheck,
	faSync,
	faTimesCircle,
} from "@fortawesome/free-solid-svg-icons"
import AppBtnMenuSmall from "../basics/button/AppBtnMenuSmall"
import { handleErrors, URL } from "../../store/actions/depannageActions"
import { normalizeApiError } from "../../store/actions/apiHelpers"
import { PdfSlot } from "../basics/interface/AppPdfs"
import { transactionEstFacture } from "../../hooks/moyenPaiement"
import CustomModal from "../basics/interface/CustomModal"
import { generalStyles } from "../styles/styles"
import { COLORS } from "../../utilities/Constants"

const PAYE_PAR_ASSURANCE = 0
const PAYE_PAR_MASTER = 2

// Revue 30.10 (D3, Jordan) : lignes de transaction de la fiche non
// enregistrées (neuves), modifiées ou retirées depuis le dernier
// enregistrement → « Enregistrer la fiche d'abord » avant de facturer.
const valeurs = (t) =>
	JSON.stringify([
		Math.round((parseFloat(t.total) || 0) * 100),
		Number(t.moyenPaie || 0),
		Number(t.payeParSelect || 0),
		Number(t.caissier || 0),
		(t.date || "").slice(0, 10),
		t.details || "",
		t.raison || "",
	])
export const compterNonEnregistrees = (formulaire, serveur) => {
	const enBase = new Map((serveur || []).map((t) => [t.id, valeurs(t)]))
	const vus = new Set()
	let n = 0
	;(formulaire || []).forEach((t) => {
		if (!Number.isInteger(t.id) || !enBase.has(t.id)) n += 1
		else {
			vus.add(t.id)
			if (enBase.get(t.id) !== valeurs(t)) n += 1
		}
	})
	enBase.forEach((_, id) => {
		if (!vus.has(id)) n += 1
	})
	return n
}

// « Facturer » commun à l'atelier et à la location (Jordan 30.10, modèle
// atelier/FacturationCustomModalV2) : dépôt du PDF de facture sur chaque ligne
// Facture (PUT api/transactionv2, qui la date au dépôt), puis
// `onFactures(transactions, toutesFacture)` → Promise (la fiche passe Clos si
// toutes les lignes sont des Factures, sinon « En attente de paiement »).
// Sans ligne à facturer : `onPasserEnAttente()`.
// `champFiche` : la fiche du PUT ({ id } atelier, { location_id },
// { depannage_id }). `onFactures` → true (enregistrée) ou la cause du refus
// (texte, revue 30.10 F1). `lignesNonEnregistrees` : lignes de transaction
// non enregistrées ou modifiées (`compterNonEnregistrees`) → enregistrer la
// fiche d'abord (F2, D3).
const FacturationModalV2 = ({
	aFacturer,
	champFiche,
	onFactures,
	onPasserEnAttente,
	onClose,
	lignesNonEnregistrees = 0,
}) => {
	const { token } = useSelector((state) => state.navInfo.login)
	const { moyenPaiements } = useSelector((state) => state.autreInfo)
	// Les transactions à facturer n'ont pas encore de fichier : `facture: null`
	// (PdfSlot affiche la tuile « + ») ; l'upload pose un pdf normalisé
	// { name, url, file, ... } dont on envoie le `.file`.
	const [transAFacturer, setTransAFacturer] = useState(
		aFacturer.map((t) => ({ ...t, facture: null })),
	)
	const [saving, setSaving] = useState(false)
	const [saved, setSaved] = useState(false)
	const [erreur, setErreur] = useState(null)
	const [selectedTrans, setSelectedTrans] = useState(0)

	const echec = (err) =>
		normalizeApiError(err).then((p) => {
			setSaving(false)
			setErreur(p.message || "La facturation a échoué.")
		})

	const saveAFacturer = () => {
		setSaving(true)
		const formData = new FormData()
		Object.entries(champFiche).forEach(([k, v]) => formData.append(k, v))
		transAFacturer.forEach((t) => {
			formData.append(`facture_${t.id}`, t.facture.file)
		})
		fetch(`${URL}api/transactionv2`, {
			method: "put",
			headers: { Authorization: `Bearer ${token}` },
			body: formData,
		})
			.then(handleErrors)
			.then((res) => res.json())
			.then((json) => {
				const transactions = Object.assign([], json)
				// Revue 30.10 (D4, Jordan) : les lignes Master (jours offerts,
				// pertes) n'empêchent pas de clore.
				const toutesFacture = transactions
					.filter((trans) => trans.payeParSelect !== PAYE_PAR_MASTER)
					.every((trans) =>
						transactionEstFacture(trans, moyenPaiements),
					)
				return Promise.resolve(
					onFactures(transactions, toutesFacture),
				).then((r) => {
					setSaving(false)
					if (r === true) setSaved(true)
					else
						setErreur(
							"Factures déposées (lignes datées), mais la fiche n'a " +
								"pas été enregistrée" +
								(typeof r === "string" && r ? ` : ${r}` : "."),
						)
				})
			})
			.catch(echec)
	}

	const updateAFacturer = (field, value) => {
		const temp = Object.assign([], transAFacturer)
		temp[selectedTrans] = { ...temp[selectedTrans], [field]: value }
		setTransAFacturer(temp)
	}
	const transCompleted = (t) => !!(t.facture && t.facture.file)
	const canSave = () => transAFacturer.every(transCompleted)

	const centered = {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 10,
		padding: "12px 0",
	}

	if (lignesNonEnregistrees > 0) {
		return (
			<CustomModal
				title={"Enregistrer la fiche d'abord"}
				onClose={onClose}
				showCloseButton={false}
				buttons={[
					{ title: "Fermer", styleName: "primary", onClick: onClose },
				]}
			>
				<div style={{ fontSize: 13, color: COLORS.textMuted }}>
					{lignesNonEnregistrees > 1
						? `${lignesNonEnregistrees} lignes de transaction ne sont pas enregistrées (neuves, modifiées ou retirées)`
						: "Une ligne de transaction n'est pas enregistrée (neuve, modifiée ou retirée)"}{" "}
					: sauvegardez la fiche, puis facturez.
				</div>
			</CustomModal>
		)
	}
	if (saving) {
		return (
			<CustomModal
				title={"Sauvegarde..."}
				showFooter={false}
				showCloseButton={false}
				onClose={onClose}
			>
				<div style={centered}>
					<FontAwesomeIcon icon={faSync} size="2x" spin />
				</div>
			</CustomModal>
		)
	}
	if (erreur) {
		return (
			<CustomModal
				title={"Erreur"}
				onClose={onClose}
				showCloseButton={false}
				buttons={[
					{ title: "Fermer", styleName: "primary", onClick: onClose },
				]}
			>
				<div style={centered}>
					<FontAwesomeIcon
						icon={faTimesCircle}
						size="2x"
						style={{ color: COLORS.danger }}
					/>
					<div>{erreur}</div>
				</div>
			</CustomModal>
		)
	}
	if (saved) {
		return (
			<CustomModal
				title={"Sauvegarde terminée"}
				onClose={onClose}
				showCloseButton={false}
				buttons={[
					{ title: "Fermer", styleName: "primary", onClick: onClose },
				]}
			>
				<div style={centered}>
					<FontAwesomeIcon
						icon={faCheck}
						size="2x"
						style={{ color: COLORS.success }}
					/>
				</div>
			</CustomModal>
		)
	}
	if (transAFacturer.length > 0) {
		const current = transAFacturer[selectedTrans]
		return (
			<CustomModal
				title={`Transaction ${current.payeParSelect === PAYE_PAR_ASSURANCE ? "assurance" : "client"}`}
				onClose={onClose}
				showCloseButton={false}
				buttons={[
					{
						title: "Fermer",
						styleName: "secondary",
						onClick: onClose,
					},
					{
						title: "Sauvegarder",
						styleName: "primary",
						onClick: saveAFacturer,
						disabled: !canSave(),
					},
				]}
			>
				{transAFacturer.length > 1 && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: 12,
							marginBottom: 12,
						}}
					>
						{transAFacturer.map((t, i) => (
							<AppBtnMenuSmall
								key={`appbtn_${i}`}
								value={i}
								title={`${i + 1}`}
								selected={selectedTrans === i}
								onclick={setSelectedTrans}
								completed={transCompleted(t)}
							/>
						))}
					</div>
				)}
				<div style={generalStyles.fieldRow}>
					<label style={generalStyles.label}>Total</label>
					<input
						style={generalStyles.inputReadonly}
						value={current.total}
						readOnly={true}
					/>
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						marginTop: 12,
					}}
				>
					<PdfSlot
						label="Facture"
						doc={current.facture}
						onUpload={(pdf) => updateAFacturer("facture", pdf)}
						onDelete={() => updateAFacturer("facture", null)}
						confirmDelete={false}
					/>
				</div>
			</CustomModal>
		)
	}
	return (
		<CustomModal
			title={"Passer en attente de paiement ?"}
			onClose={onClose}
			showCloseButton={false}
			buttons={[
				{ title: "Fermer", styleName: "secondary", onClick: onClose },
				{
					title: "Sauvegarder",
					styleName: "primary",
					onClick: onPasserEnAttente,
				},
			]}
		/>
	)
}

export default FacturationModalV2
